import React, { useEffect, useRef, useState } from "react";
import "./SearchSuggestion.css";
import TauriImage from "../../TauriImage/TauriImage";
import { getAll } from "../../../localDB";
import TauriLoader2 from "../../TauriLoader2/TauriLoader2";
import { Button } from "react-bootstrap";
import { accountMapping } from "../../../visualizations/visualizations";

const SearchSuggestion = ({ show, setShowSuggestion, search, onSelect, type = "cust", setIsNew, onAddNew }) => {
    const suggestionRef = useRef();
    const [loading, setLoading] = useState(false);
    const [itemList, setItemList] = useState([]);

    const isFirstSearch = useRef(true);

    const highlightText = (text, query) => {
        if (!text) return ""; // return empty string if text is null/undefined
        if (!query) return text;

        const regex = new RegExp(`(${query})`, "gi");
        return text.split(regex).map((part, index) =>
            part.toLowerCase() === query.toLowerCase() ? (
                <span key={index} className="highlighted-text">{part}</span>
            ) : (
                part
            )
        );
    };


    const TypeMapping = {
        cust: "customer",
        inst: "institution"
    };

    const DisplayFieldMapping = {
        cust: {
            idKey: "ID",
            codeKey: "Code",
            nameKey: "Name",
            displayName: "Customer",
            type: "Type"
        },
        inst: {
            idKey: "ID",
            codeKey: "Code",
            nameKey: "Name",
            displayName: "Institution"
        }
    };

    const addClick = () => {
        setShowSuggestion(false);
        if (onAddNew) {
            onAddNew(true);
        }
    };

    const fetchData = async (search) => {
        try {
            if (search.trim().length < 2) {
                setIsNew(false);
                setItemList([]);
                return
            }
            const targetTable = TypeMapping[type];

            setLoading(true);
            const dataList = await getAll(targetTable);
            const term = search.toLowerCase();

            // Filter the data based on search term
            const filtered = dataList.filter((item) =>
                (DisplayFieldMapping[type].type ? item[DisplayFieldMapping[type].type] !== accountMapping.Default : true) &&
                (
                    item[DisplayFieldMapping[type].codeKey]?.toLowerCase().startsWith(term) ||
                    item[DisplayFieldMapping[type].nameKey]?.toLowerCase().startsWith(term)
                )

            ).sort((a, b) => a[DisplayFieldMapping[type].nameKey].localeCompare(b[DisplayFieldMapping[type].nameKey]));


            // Only show suggestions if there are matching items
            if (filtered.length > 0) {
                setIsNew(false);
                setItemList(filtered);
            } else {
                setIsNew(true);
                setItemList([]);  // Clear the list if no match found
            }
        } catch (error) {
            console.log('Error fetching customer data!');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!show) return;

        const delay = isFirstSearch.current ? 2000 : 0;

        const delayDebounce = setTimeout(() => {
            fetchData(search);
            setShowSuggestion(true); // Show if there's valid input

            isFirstSearch.current = false;
        }, delay);

        return () => clearTimeout(delayDebounce); // Cleanup
    }, [search]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isOutsideSuggestion = suggestionRef.current && !suggestionRef.current.contains(event.target);

            if (isOutsideSuggestion) {
                setShowSuggestion(false);
            }
        };

        if (show) {
            document.addEventListener("mousedown", handleClickOutside, true);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside, true);
        };
    }, [show, setShowSuggestion]);


    if (!show || !search || search.trim() === "" || search.trim().length < 2) return null;


    return (
        <div className="opal-incidental-search-suggestion" ref={suggestionRef}>
            {loading ? (
                <TauriLoader2 width="1.5rem" height="1.5rem" borderwidth="0.2rem" color="#1D5D9B" />
            )
                : (
                    <div className="opal-incidental-search-container">
                        <div className="opal-table">
                            {itemList.map((item, index) => (
                                <div
                                    key={item[DisplayFieldMapping[type].idKey]}
                                    className={`opal-table-row ${index % 2 === 0 ? '' : 'striped-color'}`}
                                    onClick={() => onSelect(item)}
                                >
                                    <div className="opal-table-cell code-column">
                                        {highlightText(item[DisplayFieldMapping[type].codeKey]?.toUpperCase(), search)}
                                    </div>
                                    <div className="opal-table-cell name-column">
                                        {highlightText(item[DisplayFieldMapping[type].nameKey]?.toUpperCase(), search)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="no-search-section" >
                            <div className="message">Didn't find what you're looking for?</div>
                            <Button className="btn-add d-flex align-items-center" onClick={addClick}>
                                <TauriImage localImage={"/Images/flat-color-icons--plus.svg"} alt='icon' />
                                <span className="text-nowrap">Add {DisplayFieldMapping[type].displayName}</span>
                            </Button>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default SearchSuggestion;
